import React, { useEffect, useId, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

export default function SidebarCalendar({
  selectedDate,
  onChange,
  isCustomView = false,
  onToggleCustomView,
  hideNotWorking = false,
  onToggleHideNotWorking,
  showCancelled = false,
  onToggleShowCancelled,
  showCustomViewToggle = true,
  showWalkInQueueToggle = false,
  isWalkInQueueOpen = false,
  onToggleWalkInQueue,
  showHideNotWorkingToggle = true,
  showCancelledToggle = showHideNotWorkingToggle,
  showLegendButton = false,
  onOpenLegend,
  showScheduleViewSelect = false,
  scheduleViewSelectValue = 7,
  onScheduleViewSelectChange,
  scheduleViewSelectOptions = [],
  showViewAllButton = false,
  onViewAll,
  isViewAllActive = false,
  showEstimatedRevenueToggle = false,
  showEstimatedRevenue = false,
  onToggleEstimatedRevenue,
  estimatedRevenueSummary = "",
  controlsWrapperId = "",
  controlsWrapperClassName = "",
  legendWrapperId = "",
  legendWrapperClassName = "",
  showOnMobile = false,
  className = "",
}) {
  const [viewDate, setViewDate] = useState(selectedDate || new Date());
  const scheduleViewSelectId = useId();
  const estimatedRevenueSummaryRows = Array.isArray(estimatedRevenueSummary)
    ? estimatedRevenueSummary.filter(Boolean)
    : estimatedRevenueSummary
      ? [estimatedRevenueSummary]
      : [];

  useEffect(() => {
    if (selectedDate) {
      setViewDate(selectedDate);
    }
  }, [selectedDate]);

  const handleChange = (value) => {
    const date = Array.isArray(value) ? value[0] : value;
    onChange(date);
    setViewDate(date);
  };

  const goToWeekOffset = (weeks) => {
    const base = selectedDate ? new Date(selectedDate) : new Date();
    const target = new Date(base);
    target.setDate(base.getDate() + weeks * 7);
    onChange(target);
    setViewDate(target);
  };

  const containerClassName = showOnMobile
    ? "w-full lg:w-[300px] lg:min-w-[300px] border-b lg:border-r border-slate-200 bg-slate-50/80"
    : "w-[300px] min-w-[300px] border-r border-slate-200 bg-slate-50/80";

  return (
    <aside
      className={`${containerClassName} h-full min-h-0 overflow-hidden ${className}`}
    >
      <div className="flex h-full flex-col overflow-y-auto p-3">
        <Calendar
          onChange={handleChange}
          value={selectedDate}
          activeStartDate={viewDate}
          onActiveStartDateChange={({ activeStartDate }) => {
            if (activeStartDate) setViewDate(activeStartDate);
          }}
          calendarType="gregory"
          minDetail="month"
          maxDetail="month"
          selectRange={false}
          className="sidebar-calendar w-full"
          tileClassName={({ date, view }) => {
            if (view === "month") {
              const isSelected =
                selectedDate &&
                date.toDateString() === selectedDate.toDateString();
              const isToday = new Date().toDateString() === date.toDateString();

              if (isSelected) {
                return " !bg-slate-900 !text-white rounded-full";
              }
              if (isToday) {
                return " !bg-slate-200 rounded-full";
              }
            }
            return "";
          }}
        />

        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <button
            type="button"
            className="px-2 py-1 rounded border border-slate-300 hover:bg-slate-100"
            onClick={() => onChange(new Date())}
          >
            Today
          </button>
        </div>

        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-700 mb-2">
            Go to Week:
          </div>
          <div className="grid grid-cols-5 gap-2 text-xs">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((w) => (
              <button
                key={w}
                type="button"
                className="px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-100"
                onClick={() => goToWeekOffset(w)}
              >
                +{w}
              </button>
            ))}
          </div>
        </div>

        {(showLegendButton ||
          showViewAllButton ||
          showCustomViewToggle ||
          showWalkInQueueToggle ||
          showHideNotWorkingToggle ||
          showCancelledToggle ||
          showEstimatedRevenueToggle ||
          showScheduleViewSelect) && (
          <div
            id={controlsWrapperId || undefined}
            className={`mt-4 space-y-2 ${controlsWrapperClassName}`}
          >
            {showLegendButton && onOpenLegend && (
              <div id={legendWrapperId || undefined} className={legendWrapperClassName}>
                <button
                  type="button"
                  onClick={onOpenLegend}
                  className="w-full rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                >
                  Colour Key
                </button>
              </div>
            )}

            {showScheduleViewSelect && onScheduleViewSelectChange && (
              <div className="rounded border border-slate-300 bg-white px-3 py-2 text-xs">
                <label
                  htmlFor={scheduleViewSelectId}
                  className="mb-1 block text-xs font-semibold text-slate-700"
                >
                  Schedule View
                </label>
                <select
                  id={scheduleViewSelectId}
                  value={String(scheduleViewSelectValue)}
                  onChange={(event) =>
                    onScheduleViewSelectChange(Number(event.target.value))
                  }
                  className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  {(scheduleViewSelectOptions || []).map((option) => (
                    <option key={option.value} value={String(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {showViewAllButton && onViewAll && (
              <button
                type="button"
                onClick={onViewAll}
                className={`w-full rounded border px-3 py-2 text-xs font-semibold transition ${
                  isViewAllActive
                    ? "border-slate-400 bg-slate-100 text-slate-800"
                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                }`}
              >
                View All
              </button>
            )}

            {showCustomViewToggle && onToggleCustomView && (
              <label className="flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={!!isCustomView}
                  onChange={(event) => onToggleCustomView(event.target.checked)}
                />
                Custom View
              </label>
            )}

            {showWalkInQueueToggle && onToggleWalkInQueue && (
              <label className="flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={!!isWalkInQueueOpen}
                  onChange={(event) => onToggleWalkInQueue(event.target.checked)}
                />
                Walk-In Queue Slider
              </label>
            )}

            {showHideNotWorkingToggle && onToggleHideNotWorking && (
              <label className="flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={!!hideNotWorking}
                  onChange={(event) => onToggleHideNotWorking(event.target.checked)}
                />
                Hide Not Working
              </label>
            )}

            {showCancelledToggle && onToggleShowCancelled && (
              <label className="flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={!!showCancelled}
                  onChange={(event) => onToggleShowCancelled(event.target.checked)}
                />
                Show Cancelled
              </label>
            )}

            {showEstimatedRevenueToggle && onToggleEstimatedRevenue && (
              <label className="flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={!!showEstimatedRevenue}
                  onChange={(event) => onToggleEstimatedRevenue(event.target.checked)}
                />
                Show estimated revenue
              </label>
            )}

            {estimatedRevenueSummaryRows.length ? (
              <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm space-y-1">
                {estimatedRevenueSummaryRows.map((row, index) => (
                  <div key={`${row}-${index}`}>{row}</div>
                ))}
              </div>
            ) : null}
          </div>
        )}

      </div>
    </aside>
  );
}
